import React, { useEffect, useState, useContext } from "react";
import { Card, CardBody, Input } from "reactstrap";
import GaugeChart from "react-gauge-chart";
import { ColorModeContext } from "../../index";
import { request } from "../../utils/request";
import "./dashboard.scss";

const CONTROL_WEIGHTS = {
  osVersion: 10,
  securityPatches: 10,
  firewall: 10,
  antivirus: 10,
  realTimeProtection: 10,
  diskEncrypted: 10,
  secureBootEnabled: 10,
  trustedCAs: 10,
  usbDevices: 10,
  networkPrivilege: 10,
};

/* Helpers to normalize various response shapes */
const normalizeArrayResp = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (resp.data) {
    if (Array.isArray(resp.data)) return resp.data;
    if (Array.isArray(resp.data.docs)) return resp.data.docs;
  }
  if (Array.isArray(resp.docs)) return resp.docs;
  const arr = Object.values(resp).find((v) => Array.isArray(v));
  return Array.isArray(arr) ? arr : [];
};

const parseSlashDate = (s) => {
  if (!s) return null;
  if (typeof s === "number") return new Date(s);
  if (typeof s === "string") {
    const m = s.match(/\/Date\((\-?\d+)\)\//);
    if (m && m[1]) return new Date(Number(m[1]));
    const n = Number(s);
    if (!Number.isNaN(n) && String(s).length > 6) {
      return new Date(n);
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

/* Device validation heuristics (best-effort) */
const deviceChecks = {
  osVersion: (d) => {
    if (!d) return false;
    if (!d.osVersion || String(d.osVersion).trim() === "") return false;
    const patches = Array.isArray(d.securityPatches) ? d.securityPatches : [];
    const pending = Array.isArray(d.pendingUpdates) ? d.pendingUpdates : [];
    const now = Date.now();
    const maxAgeMs = 60 * 24 * 60 * 60 * 1000; // 60 days
    let latest = null;
    for (const p of patches) {
      const v = p?.InstalledOn?.value || p?.InstalledOn || p?.InstalledOn?.DateTime;
      const dt = parseSlashDate(v);
      if (dt && (!latest || dt.getTime() > latest.getTime())) latest = dt;
    }
    for (const p of pending) {
      const v = p?.InstalledOn?.value || p?.InstalledOn || p?.InstalledOn?.DateTime;
      const dt = parseSlashDate(v);
      if (dt && (!latest || dt.getTime() > latest.getTime())) latest = dt;
    }
    if (!latest) return patches.length > 0;
    return now - latest.getTime() <= maxAgeMs;
  },

  securityPatches: (d) => {
    if (!d) return false;
    const patches = Array.isArray(d.securityPatches) ? d.securityPatches : [];
    const pending = Array.isArray(d.pendingUpdates) ? d.pendingUpdates : [];
    if (patches.length === 0) return false;
    const now = Date.now();
    for (const p of pending) {
      const dt = parseSlashDate(p?.InstalledOn?.value || p?.InstalledOn);
      if (dt && dt.getTime() > now) return false;
      if (!dt && String(p?.Description || "").toLowerCase().includes("security update")) return false;
    }
    return true;
  },

  firewall: (d) => {
    if (!Array.isArray(d?.firewall) || d.firewall.length === 0) return false;
    const required = ["Domain", "Private", "Public"];
    for (const r of required) {
      const found = d.firewall.find((f) => String(f?.Name || "").toLowerCase() === r.toLowerCase());
      if (!found || Number(found?.Enabled) !== 1) return false;
    }
    return true;
  },

  antivirus: (d) => {
    const av = d?.antivirus;
    if (!av) return false;
    if (av.displayName && String(av.displayName).trim() !== "") return true;
    if (typeof av.productState !== "undefined" && Number(av.productState) > 0) return true;
    return false;
  },

  realTimeProtection: (d) => {
    if (d?.defenderRealtime) return true;
    const av = d?.antivirus;
    if (av && typeof av.productState !== "undefined") return Number(av.productState) > 0;
    return false;
  },

  diskEncrypted: (d) => {
    const b = d?.bitlocker;
    if (!b) return false;
    const vs = b.VolumeStatus ?? b.VolumeStatus;
    return Number(vs) === 1;
  },

  secureBootEnabled: (d) => {
    return !!d?.secureBoot;
  },

  trustedCAs: (d) => {
    const arr = Array.isArray(d?.trustedCAs) ? d.trustedCAs : [];
    if (arr.length === 0) return false;
    const now = Date.now();
    for (const cert of arr) {
      const subj = String(cert?.Subject || "").toLowerCase();
      if (
        subj.includes("local") ||
        subj.includes("squid") ||
        subj.includes("proxy") ||
        subj.includes("self-signed") ||
        subj.includes("local-ca") ||
        subj.includes("squid-local")
      ) {
        return false;
      }
      const na = cert?.NotAfter;
      const dt = parseSlashDate(na);
      if (dt && dt.getTime() < now) return false;
    }
    return true;
  },

  usbDevices: (d) => {
    const arr = Array.isArray(d?.usbDevices) ? d.usbDevices : [];
    if (arr.length === 0) return true;
    for (const u of arr) {
      const name = String(u?.FriendlyName || "").toLowerCase();
      const iid = String(u?.InstanceId || "").toLowerCase();
      if (name.includes("mass storage") || name.includes("external") || name.includes("external storage") || name.includes("removable")) return false;
      if (iid.includes("usb") && (name.includes("composite device") || name.includes("storage") || name.includes("disk"))) return false;
    }
    return true;
  },

  networkPrivilege: (d) => {
    const up = String(d?.userPrivilege || "").toLowerCase();
    if (!up) return false;
    return !(up.includes("administrator") || up.includes("admin"));
  },
};

const QUESTION_KEYS = Object.keys(CONTROL_WEIGHTS);
const DEFAULT_DEPARTMENTS = [
  { id: "Technical", label: "Technical" },
  { id: "Non-Technical", label: "Non-Technical" },
  { id: "Admin", label: "Admin" },
];

/* ------------------ Inject mini-widget CSS (copied from BlockedPolicies for parity) ------------- */
const injectMiniWidgetStyles = () => {
  if (document.getElementById("mini-widget-polish")) return;
  const css = `
    .mw-light-root { --bg: #fff; --tile-bg: #f9fafc; --muted: #666; --text: #0b1220; --tile-border: rgba(6,15,34,0.04); --icon-ghost: rgba(3,7,18,0.03); --shadow: 0 1px 2px rgba(0,0,0,0.06); }
    .mw-dark-root { --bg: #252b3b; --tile-bg: #2f3649; --muted: #c9c9c9; --text: #f5f5f5; --tile-border: rgba(255,255,255,0.05); --icon-ghost: rgba(255,255,255,0.03); --shadow: 0 1px 3px rgba(0,0,0,0.35); }

    .polish-card {
      border-radius: 12px;
      overflow: visible;
      position: relative;
    }
    .polish-card .polish-body {
      padding: 0.9rem;
      box-sizing: border-box;
    }
    .polish-card .card-sheen::after {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.02));
      mix-blend-mode: overlay;
      border-radius: inherit;
    }

    .polish-header { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:6px; }
    .polish-title { margin:0; font-size:1rem; font-weight:600; color:var(--text); }

    @media (max-width:720px) {
      .polish-header { flex-direction: column; align-items: flex-start; gap:6px; }
    }
  `;
  const s = document.createElement("style");
  s.id = "mini-widget-polish";
  s.appendChild(document.createTextNode(css));
  document.head.appendChild(s);
};
/* ------------------------------------------------------------------------------------------------ */

export default function RiskAnalysis() {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === "dark";

  const [departments, setDepartments] = useState(DEFAULT_DEPARTMENTS);
  const [selectedDept, setSelectedDept] = useState(DEFAULT_DEPARTMENTS[0].id);
  const [deptScoreMap, setDeptScoreMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  // percent now represents *risk* (0..1). 0 = no risk (green left), 1 = max risk (red right).
  const [percent, setPercent] = useState(0); // set directly without animation

  // select open state to toggle caret icon (approximation: focus/click => open, blur => closed)
  const [selectOpen, setSelectOpen] = useState(false);

  const cardBg = isDark ? "#252b3b" : "#fff";
  const borderCol = isDark ? "none" : "1px solid #e8eef8";
  const textColor = isDark ? "#f5f5f5" : "#0b1d51";

  // REVERSED palette: green (left) → red (right)
  const gaugeColors = ["#16a34a", "#a3e635", "#f1c40f", "#ff8a00", "#e74c3c"];

  // load questionnaire, devices, users; compute scores
  const loadAll = async () => {
    setLoading(true);
    try {
      const [qResp, devResp, userResp] = await Promise.all([
        request("questionnaire", { method: "GET" }).catch(() => null),
        request("devices", { method: "GET" }).catch(() => null),
        request("user", { method: "GET" }).catch(() => null),
      ]);

      // normalize questionnaire (single doc)
      let questionnaire = null;
      if (!qResp) questionnaire = null;
      else if (qResp.data && typeof qResp.data === "object") questionnaire = qResp.data;
      else if (Array.isArray(qResp) && qResp.length > 0) questionnaire = qResp[0];
      else if (typeof qResp === "object") questionnaire = qResp;
      else questionnaire = null;

      const devices = normalizeArrayResp(devResp);
      const users = normalizeArrayResp(userResp);

      // userId -> department
      const userDeptById = new Map();
      const deptSet = new Set();
      for (const u of users) {
        if (!u) continue;
        const id = u._id || u.id || u.userId || (u._id && u._id.toString && u._id.toString());
        const dept = u.department || u.departmentName || u.dept || "Unassigned";
        if (id) userDeptById.set(String(id), String(dept));
        deptSet.add(String(dept));
      }

      const resolvedDepartments = deptSet.size > 0 ? Array.from(deptSet).map((d) => ({ id: d, label: d })) : DEFAULT_DEPARTMENTS;
      setDepartments(resolvedDepartments);
      if (!resolvedDepartments.find((d) => d.id === selectedDept)) {
        setSelectedDept(resolvedDepartments[0].id);
      }

      // keys applicable from questionnaire (those marked "YES")
      const applicableKeys = QUESTION_KEYS.filter((k) => String(questionnaire?.[k] ?? "").toUpperCase() === "YES");
      const totalApplicableWeight = applicableKeys.reduce((s, k) => s + (CONTROL_WEIGHTS[k] || 0), 0);

      // if none applicable, treat denom as 100 (fully compliant) to avoid division by zero
      const denom = totalApplicableWeight === 0 ? 100 : totalApplicableWeight;

      // aggregate per-department
      const deptAcc = new Map(); // dept -> { sum: x, count: n }

      for (const d of devices) {
        const uid = String(d?.userId || d?.user || d?.user_id || "");
        const dept = userDeptById.get(uid) || "Unassigned";

        let awarded = 0;
        for (const k of applicableKeys) {
          try {
            const ok = !!deviceChecks[k](d);
            if (ok) awarded += CONTROL_WEIGHTS[k] || 0;
          } catch (e) {
            // treat check errors as not satisfied
          }
        }

        const deviceScore = denom === 0 ? 1.0 : awarded / denom;
        const prev = deptAcc.get(dept) || { sum: 0, count: 0 };
        prev.sum += deviceScore;
        prev.count += 1;
        deptAcc.set(dept, prev);
      }

      // compute final dept scores (average device score)
      const final = {};
      for (const dept of resolvedDepartments) {
        const v = deptAcc.get(dept.id);
        final[dept.id] = v && v.count > 0 ? v.sum / v.count : 0;
      }
      for (const [dept, agg] of deptAcc.entries()) {
        if (!(dept in final)) final[dept] = agg.count > 0 ? agg.sum / agg.count : 0;
      }

      setDeptScoreMap(final);
      setLastFetchedAt(new Date());

      // set percent as *risk* = 1 - secureScore (so gauge shows risk, left green => right red)
      const curSecure = final[selectedDept] ?? 0;
      const curRisk = Math.max(0, Math.min(1, 1 - curSecure));
      setPercent(curRisk);
    } catch (err) {
      console.error("RiskAnalysis loadAll error:", err);
      setDeptScoreMap({});
      setPercent(0);
    } finally {
      setLoading(false);
    }
  };

  // initial load — NO periodic refresh (removed setInterval). The component will update only on mount
  useEffect(() => {
    injectMiniWidgetStyles(); // match BlockedPolicies heading / polish styles
    loadAll();
    // intentionally no interval — data loads once on mount and will refresh only when the user reloads the page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // when selected dept changes, set percent immediately to risk (no animation)
  useEffect(() => {
    const score = deptScoreMap[selectedDept] ?? 0;
    const risk = Math.max(0, Math.min(1, 1 - score));
    setPercent(risk);
  }, [selectedDept, deptScoreMap]);

  const scoreForSelected = deptScoreMap[selectedDept] ?? 0;
  // risk percent = 100 * (1 - secureScore)
  const riskPercent = Math.round((1 - (scoreForSelected || 0)) * 100);

  // color the risk text: green for low risk, amber for medium, red for high
  const riskColor = scoreForSelected >= 0.85 ? "#16a34a" : scoreForSelected >= 0.6 ? "#f59e0b" : "#ef4444";

  const rootClass = mode === "dark" ? "mw-dark-root" : "mw-light-root";

  // ---------- styling improvements (kept inline for easy copy/paste) ----------
  const gaugeWrapper = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 8,
    width: "100%",
  };

  // IMPORTANT: keep the overall card compact — remove forcing full-height and only make the gauge itself a bit larger
  const gaugeContainer = {
    width: 360, // slightly larger gauge only
    maxWidth: "360px",
    minHeight: 220,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };

  const riskTextStyle = {
    fontSize: 20,
    fontWeight: 700,
    color: riskColor,
    letterSpacing: "0.2px",
  };

  const deptTextStyle = {
    fontSize: 15,
    color: textColor,
    marginTop: 6,
    fontWeight: 500,
  };

  // ---------------------------------------------------------------------------

  return (
    <div className={`widget-card risk-widget ${rootClass}`} style={{ width: "100%" }}>
      <Card
        className="polish-card"
        style={{
          backgroundColor: cardBg,
          border: isDark ? "none" : "1px solid #e6e9ee",
          borderRadius: 12,
        }}
      >
        {/* sheen overlay element (keeps the same visual sheen as other polished cards) */}
        <div className="card-sheen" style={{ position: "absolute", inset: 0, borderRadius: 12, pointerEvents: "none" }} />

        <CardBody className="polish-body" style={{ display: "flex", flexDirection: "column", minHeight: 0, padding: 0, boxSizing: "border-box" }}>
          <div
            className="polish-header"
            style={{
              padding: "0.8rem 0.8rem 0 0.8rem",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* LEFT → Risk Analysis (polish title to match BlockedPolicies) */}
            <h5
              className="polish-title"
              style={{
                color: isDark ? "#f5f5f5" : "#0b1d51",
                whiteSpace: "nowrap",
                margin: 0,
                // keep heading vertically centered with the select
                alignSelf: "center",
              }}
            >
              Risk Analysis
            </h5>

            {/* RIGHT → Department select (size & style matched to BlockedPolicies). */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                minWidth: 160,
                marginTop: 0,
              }}
            >
              {/* wrapper positions carets inside the select */}
              <div style={{ position: "relative", display: "inline-block" }}>
                <Input
                  type="select"
                  value={selectedDept}
                  onChange={(e) => setSelectedDept(e.target.value)}
                  onFocus={() => setSelectOpen(true)}
                  onClick={() => setSelectOpen(true)}
                  onBlur={() => setSelectOpen(false)}
                  onKeyDown={(e) => {
                    // handle common keys that close the native select
                    if (e.key === "Escape" || e.key === "Enter") {
                      setSelectOpen(false);
                    }
                  }}
                  aria-expanded={selectOpen}
                  style={{
                    width: 150,
                    borderRadius: 6,
                    padding: "6px 8px",
                    paddingRight: 38, // leave room for caret
                    background: isDark ? "#1f2937" : "#ffffff",
                    color: isDark ? "#f5f5f5" : "#0b1d51",
                    borderColor: "rgba(0,0,0,0.06)",
                    WebkitAppearance: "none",
                    MozAppearance: "none",
                    appearance: "none",
                  }}
                >
                  {departments.map((d) => (
                    <option key={d.id || d} value={d.id || d}>
                      {d.label || (d === "all" ? "All Departments" : d)}
                    </option>
                  ))}
                </Input>

                {/* single caret shown depending on open/closed state */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: isDark ? "#e6eef8" : "#0b1d51",
                    opacity: 0.9,
                  }}
                >
                  {selectOpen ? (
                    // upward caret when open
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 6L6 2L10 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    // downward caret when closed
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 2L6 6L10 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* content area */}
          <div style={{ padding: "0.9rem", display: "flex", flexDirection: "column" }}>
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: 24 }}>
                <div style={{ color: textColor }}>Loading…</div>
              </div>
            ) : (
              <>
                <div style={gaugeWrapper}>
                  <div style={gaugeContainer}>
                    <GaugeChart
                      id="risk-gauge-chart"
                      nrOfLevels={5}
                      colors={gaugeColors} // green (left) -> red (right)
                      arcWidth={0.28}
                      percent={percent} // percent now represents RISK (0..1)
                      textColor={isDark ? "#fff" : "#222"}
                      animate={false}
                      needleColor="#333"
                      needleBaseColor="#333"
                      hideText={true}
                      cornerRadius={3}
                      style={{ width: "100%" }} // fill the container we defined above
                    />
                  </div>
                </div>

                <div style={{ textAlign: "center", marginTop: 12 }}>
                  <div style={riskTextStyle}>{riskPercent}% risk</div>
                  <div style={deptTextStyle}>
                    {departments.find((d) => d.id === selectedDept)?.label || selectedDept} Department
                  </div>
                </div>
              </>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
